import type { WebConversationDecision } from '../pages/api/conversation-decisions';

/**
 * The proposals card: several proposed tasks (a connector deep dive's findings), each held on an APPROVE
 * decision, listed under the Maestro reply that offers them, with a checkbox each. The meeting-approval
 * pattern generalised to many decisions: the row's `metadata.proposalsCard` names them, their live state
 * comes from `/api/conversation-decisions`, and the answer goes out on the shared verdict route.
 *
 * The marker alone never renders buttons — a closed or unknown decision must not look open.
 *
 * Mirrors iOS's and Android's `ProposalsCard`.
 */
export interface ProposalsCardMarker {
  decisionIds: string[];
}

export const PROPOSALS_CARD_LIMIT = 10;

/** Read `metadata.proposalsCard` off a row. Fail-closed: no usable id, no marker. */
export function readProposalsCardMarker(metadata: unknown): ProposalsCardMarker | undefined {
  const raw = (metadata as { proposalsCard?: unknown } | null | undefined)?.proposalsCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const ids = (raw as { decisionIds?: unknown }).decisionIds;
  if (!Array.isArray(ids)) return undefined;
  const decisionIds = [
    ...new Set(
      ids
        .filter((id): id is string => typeof id === 'string')
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ].slice(0, PROPOSALS_CARD_LIMIT);
  return decisionIds.length > 0 ? { decisionIds } : undefined;
}

export type ProposalRow = WebConversationDecision & { proposal: NonNullable<WebConversationDecision['proposal']> };

/** The card's rows: marker order, each matched to a live row that carries `proposal`. Empty → no card. */
export function assembleProposalRows(marker: ProposalsCardMarker, rows: ReadonlyArray<WebConversationDecision>): ProposalRow[] {
  const byId = new Map(rows.filter((row): row is ProposalRow => !!row.proposal).map((row) => [row.decision.id, row]));
  return marker.decisionIds.flatMap((id) => byId.get(id) ?? []);
}

/** Whether this chat holds a proposals row — the condition for asking `conversation-decisions` for closed proposals. */
export function threadHasProposalsCard(messages: ReadonlyArray<{ role?: string; proposalsCard?: ProposalsCardMarker }>): boolean {
  return messages.some((message) => message.role === 'ASSISTANT' && !!message.proposalsCard);
}

export type ProposalState = 'open' | 'approved' | 'declined' | 'closed';

/** Only an explicit OPEN is answerable; any other status reads as closed, so an unknown one gets no checkbox. */
export function proposalState(row: WebConversationDecision): ProposalState {
  const resolution = row.decision.outcome?.resolution?.trim().toUpperCase();
  if (resolution === 'ACCEPTED') return 'approved';
  if (resolution === 'REFUSED') return 'declined';
  return row.decision.status?.trim().toUpperCase() === 'OPEN' ? 'open' : 'closed';
}

export const PROPOSAL_STATE_KEYS: Record<Exclude<ProposalState, 'open'>, string> = {
  approved: 'proposalsCard.approved',
  declined: 'proposalsCard.declined',
  closed: 'proposalsCard.closed',
};

export interface ProposalVerdict {
  decisionId: string;
  resolution: 'ACCEPTED' | 'REFUSED';
}

/** "Approve selected": one verdict per OPEN row — ticked accepts, unticked refuses. Closed rows send nothing. */
export function planProposalVerdicts(rows: ReadonlyArray<ProposalRow>, ticked: ReadonlySet<string>): ProposalVerdict[] {
  return rows
    .filter((row) => proposalState(row) === 'open')
    .map((row) => ({ decisionId: row.decision.id, resolution: ticked.has(row.decision.id) ? 'ACCEPTED' : 'REFUSED' }));
}

/** "Decline all": a refusal for every OPEN row, whatever is ticked. Closed rows send nothing. */
export function planDeclineAll(rows: ReadonlyArray<ProposalRow>): ProposalVerdict[] {
  return rows.filter((row) => proposalState(row) === 'open').map((row) => ({ decisionId: row.decision.id, resolution: 'REFUSED' }));
}

/** "Approve selected" needs a tick — or an open row whose last verdict failed, so the failure can be retried. */
export function canApproveSelected(openRows: ReadonlyArray<ProposalRow>, ticked: ReadonlySet<string>, failed: ReadonlySet<string>): boolean {
  return openRows.some((row) => ticked.has(row.decision.id) || failed.has(row.decision.id));
}

/** The header's subtitle source: the first row's app, when it names one. */
export function proposalsCardSource(rows: ReadonlyArray<ProposalRow>): string | undefined {
  return rows[0]?.proposal.source?.trim() || undefined;
}
